import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { OverviewViewMode, OverviewViewModeStrings } from 'store/storeTypes';
import { styles } from './TimeViewModeToggle.styles';

interface TimeViewModeToggleProps {
  timeViewMode: OverviewViewModeStrings;
  isVisible: boolean;
  isProductionHoursConfigured: boolean;
  isLoading: boolean;
  setTimeViewMode: (value: OverviewViewModeStrings) => void;
}

const TimeViewModeToggle: React.FC<TimeViewModeToggleProps> = ({
  timeViewMode,
  isVisible,
  isProductionHoursConfigured,
  isLoading,
  setTimeViewMode,
}) => {
  const { t } = useTranslation();

  const handleChange = (event: React.MouseEvent<HTMLElement>, newVal: OverviewViewModeStrings) => {
    if (newVal !== null) {
      setTimeViewMode(newVal);
    }
  };

  if (!isVisible) {
    return null;
  }

  const productionButton = (
    <ToggleButton
      sx={styles.toggleButton}
      value={OverviewViewMode.Production}
      disabled={isLoading || !isProductionHoursConfigured}
      data-testid="view-mode-production-button">
      {t('settings.production')}
    </ToggleButton>
  );

  const toggleGroup = (
    <ToggleButtonGroup
      sx={styles.toggleButtonGroup}
      exclusive
      disabled={isLoading}
      value={timeViewMode || OverviewViewMode.TwentyFourHours}
      onChange={handleChange}
      data-testid="view-mode-toggle">
      <ToggleButton
        sx={styles.toggleButton}
        value={OverviewViewMode.TwentyFourHours}
        data-testid="view-mode-24h-button">
        {t('settings.twentyFourHours')}
      </ToggleButton>
      {isProductionHoursConfigured || isLoading ? (
        productionButton
      ) : (
        <Tooltip title={t('settings.productionHoursNotSet')} placement="top" arrow>
          <span>{productionButton}</span>
        </Tooltip>
      )}
    </ToggleButtonGroup>
  );

  return (
    <Box sx={styles.wrapper}>
      <Box component="span" sx={styles.label}>
        {t('settings.viewMode')}
      </Box>
      {isLoading ? (
        <Skeleton variant="rounded" data-testid="view-mode-toggle-skeleton">
          {toggleGroup}
        </Skeleton>
      ) : (
        toggleGroup
      )}
    </Box>
  );
};

export default TimeViewModeToggle;
